import { onBeforeUnmount, onMounted, reactive, type Ref } from 'vue'

/** Marca que usa execCommand('fontSize') para señalar lo que el usuario acaba de seleccionar. */
const FONT_SIZE_MARKER = '7'

/** Tipos de letra de la barra. El valor es lo que viaja en el correo. */
export const FONT_FAMILIES = [
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Calibri', value: 'Calibri, Carlito, sans-serif' },
  { label: 'Courier New', value: '"Courier New", Courier, monospace' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Montserrat', value: 'Montserrat, Arial, sans-serif' },
  { label: 'Segoe UI', value: '"Segoe UI", Arial, sans-serif' },
  { label: 'Tahoma', value: 'Tahoma, Geneva, sans-serif' },
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
] as const

/** Tamaños en píxeles, como los muestra Outlook. */
export const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36] as const

/** Colores de letra y de resaltado. Incluye los de la marca. */
export const TEXT_COLORS = [
  '#000000', '#161535', '#434343', '#666666', '#999999', '#ffffff',
  '#c0392b', '#e74c3c', '#e67e22', '#f1c40f', '#2ecc71', '#00ffce',
  '#1abc9c', '#3498db', '#2980b9', '#8e44ad', '#d81b60', '#795548',
] as const

/**
 * Comandos de la barra en nombres propios, no los de execCommand: el editor de
 * firmas corre sobre Tiptap y usa la misma barra con otro motor debajo.
 */
export const TOGGLE_COMMANDS = {
  bold: 'bold',
  italic: 'italic',
  underline: 'underline',
  strike: 'strikeThrough',
  bulletList: 'insertUnorderedList',
  orderedList: 'insertOrderedList',
  alignLeft: 'justifyLeft',
  alignCenter: 'justifyCenter',
  alignRight: 'justifyRight',
} as const

export type ToggleCommand = keyof typeof TOGGLE_COMMANDS

/** Estado de cada botón de encender/apagar de la barra. */
export type ActiveFormats = Record<ToggleCommand, boolean>

/**
 * Convierte en píxeles el tamaño que dejó el navegador.
 *
 * execCommand('fontSize') solo entiende los tamaños 1..7 del HTML viejo, así
 * que la barra aplica el 7 como marca y aquí se reemplaza por el tamaño real.
 * El color y la fuente que traía el <font> se conservan como estilo, porque
 * combinar color y tamaño sobre la misma selección los deja en la misma etiqueta.
 */
export function applyPixelFontSize(root: HTMLElement, px: number): void {
  const marked = root.querySelectorAll(`font[size="${FONT_SIZE_MARKER}"]`)
  if (marked.length === 0) return

  const selection = document.getSelection()
  const wasCollapsed = selection?.isCollapsed ?? true
  const replacements: HTMLSpanElement[] = []

  marked.forEach((font) => {
    const span = document.createElement('span')
    span.style.fontSize = `${px}px`

    const color = font.getAttribute('color')
    if (color) span.style.color = color

    const face = font.getAttribute('face')
    if (face) span.style.fontFamily = face

    while (font.firstChild) span.appendChild(font.firstChild)
    font.replaceWith(span)
    replacements.push(span)
  })

  restoreSelection(replacements, wasCollapsed)
}

/**
 * Devuelve la selección al texto que se acaba de reemplazar.
 *
 * Cambiar el <font> por un <span> saca la selección del documento. Sin esto,
 * elegir tamaño y enseguida color dejaría el color sin aplicar, y al escribir
 * el cursor se perdería justo después de teclear la primera letra.
 */
function restoreSelection(spans: HTMLSpanElement[], collapsed: boolean): void {
  const selection = document.getSelection()
  const first = spans[0]
  const last = spans[spans.length - 1]
  if (!selection || !first || !last) return

  const range = document.createRange()
  if (collapsed) {
    range.selectNodeContents(last)
    range.collapse(false)
  } else {
    range.setStartBefore(first)
    range.setEndAfter(last)
  }

  selection.removeAllRanges()
  selection.addRange(range)
}

/** Esquemas que el usuario puede escribir completos; el resto se asume web. */
const URL_SCHEMES = /^(https?:|mailto:|tel:)/i

/**
 * Completa la dirección que escribió el usuario.
 *
 * Escribir "kila.co" es lo normal, y sin esquema el navegador lo tomaría como
 * ruta relativa: el enlace del correo llevaría al destinatario a ningún lado.
 */
export function normalizeUrl(input: string): string {
  const url = input.trim()
  if (!url) return ''
  return URL_SCHEMES.test(url) ? url : `https://${url}`
}

/** Arma el enlace escapando el texto, que va derecho al HTML del correo. */
export function buildLinkHtml(url: string, text: string): string {
  const label = text.trim() || url
  const escaped = label
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
  return `<a href="${url}">${escaped}</a>`
}

/**
 * Busca el enlace donde está el cursor, dentro del editor.
 *
 * Sirve para que el diálogo llegue con los campos llenos cuando el usuario
 * vuelve sobre un enlace que ya existe, en vez de obligarlo a rehacerlo.
 */
export function findLinkInSelection(root: HTMLElement): HTMLAnchorElement | null {
  const selection = document.getSelection()
  if (!selection || selection.rangeCount === 0) return null

  const start = selection.getRangeAt(0).commonAncestorContainer
  if (!root.contains(start)) return null

  let node: Node | null = start
  while (node && node !== root) {
    if (node instanceof HTMLAnchorElement) return node
    node = node.parentNode
  }
  return null
}

/**
 * Barra de formato para los editores contenteditable del correo.
 *
 * Trabaja con execCommand porque el HTML que produce lleva el estilo en línea,
 * que es lo único que respetan Outlook y Gmail al recibir el mensaje.
 *
 * @param editorRef  el contenteditable sobre el que se aplica el formato
 * @param onChange   se llama cuando el contenido cambió, para que el editor
 *                   sincronice su modelo (execCommand no siempre emite `input`,
 *                   y el ajuste de tamaño toca el DOM por fuera del navegador)
 */
export function useRichTextFormat(editorRef: Ref<HTMLElement | null>, onChange: () => void) {
  const activeFormats = reactive<ActiveFormats>({
    bold: false,
    italic: false,
    underline: false,
    strike: false,
    bulletList: false,
    orderedList: false,
    alignLeft: false,
    alignCenter: false,
    alignRight: false,
  })

  function isInsideEditor(): boolean {
    const selection = document.getSelection()
    if (!selection || selection.rangeCount === 0 || !editorRef.value) return false
    return editorRef.value.contains(selection.getRangeAt(0).commonAncestorContainer)
  }

  /**
   * Última selección que estuvo dentro del editor.
   *
   * El diálogo de enlace tiene campos de texto, y al enfocarlos el navegador
   * borra la selección del contenteditable: solo hay una por documento. Guardarla
   * en cada movimiento evita tener que coordinar quién avisa que se abrió.
   */
  let savedRange: Range | null = null

  /** Refleja en la barra el formato donde está el cursor, como hace Outlook. */
  function refreshActiveFormats(): void {
    const inside = isInsideEditor()
    if (inside) savedRange = document.getSelection()!.getRangeAt(0).cloneRange()

    if (typeof document.queryCommandState !== 'function') return

    ;(Object.keys(TOGGLE_COMMANDS) as ToggleCommand[]).forEach((command) => {
      activeFormats[command] = inside && document.queryCommandState(TOGGLE_COMMANDS[command])
    })
  }

  function run(command: string, value?: string): void {
    editorRef.value?.focus()
    document.execCommand(command, false, value)
  }

  /** Aplica un comando de encender/apagar (negrilla, viñetas, alineación...). */
  function toggle(command: ToggleCommand): void {
    run(TOGGLE_COMMANDS[command])
    refreshActiveFormats()
    onChange()
  }

  function setFontFamily(family: string): void {
    run('styleWithCSS', 'true')
    run('fontName', family)
    onChange()
  }

  /** Último tamaño escogido, para lo que el usuario escriba a continuación. */
  let chosenFontSize: number | null = null

  /**
   * El tamaño se aplica en dos pasos: la marca del navegador y su reemplazo por
   * píxeles. styleWithCSS queda apagado a propósito para que salga la marca
   * <font size="7"> y no un tamaño con palabra clave, que no se puede traducir.
   */
  function setFontSize(px: number): void {
    chosenFontSize = px
    run('styleWithCSS', 'false')
    run('fontSize', FONT_SIZE_MARKER)
    run('styleWithCSS', 'true')
    normalizeFontSize()
    onChange()
  }

  /**
   * Convierte las marcas que el navegador deja al escribir.
   *
   * Con el cursor sin selección, elegir el tamaño no marca nada en el momento:
   * la marca aparece cuando el usuario escribe. El editor llama esto en cada
   * cambio para que ese texto salga del tamaño escogido y no del tamaño 7.
   */
  function normalizeFontSize(): void {
    if (chosenFontSize === null || !editorRef.value) return
    applyPixelFontSize(editorRef.value, chosenFontSize)
  }

  function setTextColor(color: string): void {
    run('styleWithCSS', 'true')
    run('foreColor', color)
    onChange()
  }

  /** Resaltado: hiliteColor es el comando estándar; Safari solo entiende backColor. */
  function setHighlight(color: string | null): void {
    run('styleWithCSS', 'true')
    const value = color ?? 'transparent'
    if (!document.execCommand('hiliteColor', false, value)) {
      run('backColor', value)
    }
    onChange()
  }

  function clearFormat(): void {
    run('removeFormat')
    refreshActiveFormats()
    onChange()
  }

  function restoreSavedSelection(): void {
    if (!savedRange) return
    const selection = document.getSelection()
    if (!selection) return
    selection.removeAllRanges()
    selection.addRange(savedRange)
  }

  /** El enlace donde está el cursor, para llegar con los campos llenos. */
  function linkAtSelection(): HTMLAnchorElement | null {
    if (!editorRef.value) return null
    restoreSavedSelection()
    return findLinkInSelection(editorRef.value)
  }

  /** Selecciona el enlace completo, para reemplazarlo o quitarlo de una. */
  function selectLink(link: HTMLAnchorElement): void {
    const selection = document.getSelection()
    if (!selection) return
    const range = document.createRange()
    range.selectNode(link)
    selection.removeAllRanges()
    selection.addRange(range)
  }

  function insertLink(url: string, text: string): void {
    const href = normalizeUrl(url)
    if (!href || !editorRef.value) return

    editorRef.value.focus()
    restoreSavedSelection()

    const existing = findLinkInSelection(editorRef.value)
    if (existing) selectLink(existing)

    // Con el mismo texto se usa createLink, que respeta el formato que ya tenía;
    // si el texto cambió o no había selección, hay que escribir el enlace entero.
    const selected = document.getSelection()?.toString() ?? ''
    if (selected && selected === text.trim()) {
      document.execCommand('createLink', false, href)
    } else {
      document.execCommand('insertHTML', false, buildLinkHtml(href, text))
    }
    onChange()
  }

  function removeLink(): void {
    if (!editorRef.value) return
    editorRef.value.focus()
    restoreSavedSelection()

    const existing = findLinkInSelection(editorRef.value)
    if (existing) selectLink(existing)

    document.execCommand('unlink')
    onChange()
  }

  onMounted(() => document.addEventListener('selectionchange', refreshActiveFormats))
  onBeforeUnmount(() => document.removeEventListener('selectionchange', refreshActiveFormats))

  return {
    activeFormats,
    toggle,
    setFontFamily,
    setFontSize,
    normalizeFontSize,
    setTextColor,
    setHighlight,
    clearFormat,
    insertLink,
    removeLink,
    linkAtSelection,
  }
}
